import React, { Component } from 'react'
import { AxiosResponse } from 'axios'
import { JsonEditor } from 'json-edit-react'

import {
    Grid,
    Theme,
    css,
    Section,
    TRow,
    GridCell,
    Button,
    Badge,
    Spinner,
    PopupHint,
    Icon,
} from "@bdfx/mergado-ui-kit"

import {MCP_call, MCP_readResource} from 'app/helpers/api'
import { forEach } from 'lodash'
import { URL } from 'app/config/api-config'
import { iconDisabledColor } from 'template-edit/components/tools-panel'

interface Props {
    selectedProjectId: number
    selectedTemplateId: number
}

type ArgumentsType = {
    project_id?: number
    template_id?: number
    name?: string
    width?: number
    height?: number
    jsonUrl?: string
}

interface State {
    toolsList: MCPResponse
    resourcesList: MCPResponse
    request: MCPTool
    args: ArgumentsType
    output: MCPResponse
    statusCode: number
    isEditing: boolean
    isLoadingTools: boolean
    isLoadingResources: boolean
    copiedText: string
    resource: MCPResource
}

export type MCPTool = {
    "name": string
    "title": string
    "description": string
    "inputSchema": {
        "properties": object
        "type": 'object' | 'string' | 'number' | 'array'
        "required": string[]
    },
    "annotations": object
}

export type MCPResource = {
    "name": string
    "title": string
    "description": string
    "uri": string
    "mimeType": string
}

type ContentType = {
    type: string
    text: string
}
export type MCPResponse = {
  "jsonrpc": "2.0"
  "id": number
    "result": {
      "resources": MCPResource[]
      "tools": MCPTool[]
      "content": ContentType[]
      "isError": boolean
  }
}

class MCP extends Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = {
            toolsList: null,
            resourcesList: null,
            resource: null,
            request: null,
            args: {
                project_id: props.selectedProjectId,
            },
            output: null,
            statusCode: null,
            isEditing: null,
            isLoadingTools: null,
            isLoadingResources: null,
            copiedText: null,
        }
    }

    copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        this.setState({ copiedText: text })
    }

    componentDidMount(): void {
        //tools:
        this.setState({ isLoadingTools: true })
        MCP_call('tools/list', { projectId: this.props.selectedProjectId }, null)
            .then((response: AxiosResponse<MCPResponse>) => {
                this.setState({ toolsList: response.data })
            })
            .catch((e) => {
                this.setState({ toolsList: e.response.data })
            })
            .finally(() => this.setState({ isLoadingTools: false }))


        //resources:
        this.setState({ isLoadingResources: true })
        MCP_call('resources/list', { projectId: this.props.selectedProjectId }, null)
            .then((response: AxiosResponse<MCPResponse>) => {
                this.setState({ resourcesList: response.data })
            })
            .catch((e) => {
                this.setState({ resourcesList: e.response.data })
            })
            .finally(() => this.setState({ isLoadingResources: false }))
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.selectedTemplateId !== this.props.selectedTemplateId) {
            this.setState({
                args: {
                    ...this.state.args,
                    project_id: parseInt(`${this.props.selectedProjectId}`),
                    template_id: typeof this.state.args?.template_id !== 'undefined' ? parseInt(`${this.props.selectedTemplateId}`) : undefined,
                },
                output: null,
                statusCode: null,
                isEditing: null,
            })
        }
    }

    sendRequest = (request: MCPTool) => {
        const { args } = this.state

        console.debug(`sending request for ${request.name}`, args)
        MCP_call('tools/call', args, request.name)
            .then((response: AxiosResponse<MCPResponse>) => {
                this.setState({
                    output: response.data,
                    statusCode: response.status,
                })
            })
            .catch((e) => {
                this.setState({
                    output: e.response?.data,
                    statusCode: e.response?.status,
                })
            })
    }

    readResource = () => {
        const { resource, args } = this.state

        console.debug(`resource ${resource.name} ${resource.uri}`)
        MCP_readResource(resource.uri, args)
            .then((response: AxiosResponse<MCPResponse>) => {
                this.setState({
                    output: response.data,
                    statusCode: response.status,
                })
            })
            .catch((e) => {
                this.setState({
                    output: e.response?.data,
                    statusCode: e.response?.status,
                })
            })
    }

    setResource = (resource: MCPResource) => {
        this.setState({
            resource: resource,
            request: null,
            statusCode: null,
            output: null,
            isEditing: null,
            args: {
                project_id: this.props.selectedProjectId,
            }
        })
    }

    setRequest = (tool: MCPTool) => {
        const currentdate = new Date();
        const currentdateStr = `${currentdate.getFullYear()}-${`${currentdate.getMonth() + 1}`.padStart(2, '0')}-${`${currentdate.getDate()}`.padStart(2, '0')}`

        let args = {}
        forEach(tool.inputSchema.properties, (value, key) => {
            console.debug(`value from schema for ${key}`, value)
            switch (key) {
                case 'text_content':
                    args[key] = 'hello'
                    break;
                case 'text_fontsize':
                    args[key] = 20;
                    break;
                case 'text_color':
                    args[key] = '#0000ff';
                    break;
                case 'from_project_id':
                case 'project_id':
                    args[key] = this.props.selectedProjectId ?? 0
                    break;
                case 'template_id':
                    args[key] = this.props.selectedTemplateId ?? 0
                    break;
                case 'ai_type':
                case 'keep_original_size':
                    args[key] = null
                    break;
                case 'size_id':
                    args[key] = ''
                    break;
                case 'is_active':
                    args[key] = 1
                    break;
                case 'background':
                    args[key] = '#ffffff'
                    break;
                case 'name':
                case 'json_url':
                case 'image_url':
                    args[key] = ''
                    break;
                case 'ai_params_enabled':
                case 'visual_similarity_enabled':
                    args[key] = true
                    break;
                case 'visual_similarity_n':
                    args[key] = 5
                    break;
                case 'visual_similarity_threshold':
                    args[key] = .5
                    break;
                case 'visual_similarity_templateids':
                    args[key] = [0]
                    break;
                case 'width':
                    args[key] = 800
                    break;
                case 'height':
                    args[key] = 120
                    break;
                case 'x':
                case 'y':
                    args[key] = 100
                    break;
                case 'from':
                    args[key] = `${currentdateStr} 00:00:00`
                    break;
                case 'to':
                    args[key] = `${currentdateStr} 23:59:59`
                    break;
                case 'new_order':
                    args[key] = []
                    break;
                case 'queries':
                    args[key] = []
                    break;
                default:
                    args[key] = null
                    break;
            }
        })

        this.setState({
            request: tool,
            statusCode: null,
            output: null,
            isEditing: null,
            args: args,
            resource: null,
        })
    }

    render() {
        const { request, resource, toolsList, resourcesList, args, output, statusCode, isEditing, isLoadingTools, isLoadingResources, copiedText } = this.state

        const mcpRoot = URL.rootUrl.replace('api', 'mcp')

        const toolsListCurlCommand = `curl -X POST -H "Content-Type: application/json" -H "Accept: application/json" -d '{
    "jsonrpc": "2.0",
    "method": "tools/list",
    "params": {
      "protocolVersion": "2025-03-26",
      "clientInfo": {
        "name": "curl-test",
        "version": "1.0.0"
      }
    },
    "id": 1
  }' "${mcpRoot}"`

        const callCurlCommand = (toolName: string, argumentsJson: string) => `curl -X POST -H "Authorization: Bearer $MCP_TOKEN" -H "Content-Type: application/json" -H "Accept: application/json" -d '{
  "jsonrpc": "2.0",
  "method": "tools/call",
  "params": {
    "name": "${toolName}",
    "arguments": ${argumentsJson}
  },
  "id": 3
}' "${mcpRoot}"`

        const readResourceCurlCommand = (resource: MCPResource) => `curl -X POST -H "Authorization: Bearer $MCP_TOKEN" -H "Content-Type: application/json" -H "Accept: application/json" -d '{
  "jsonrpc": "2.0",
  "method": "resources/read",
  "params": {
    "uri": "${resource.name}"
  },
  "id": 3
}' "${mcpRoot}"`

        const addToClaudeCommand = `claude mcp add feed-image-editor --transport http ${mcpRoot} --header "Authorization: Bearer mergado_pat_..."`
        const renderCurlCommandHelp = (toolName: string, argumentsJson: string) => <PopupHint style={{ position: 'absolute', top: '0px', right: '5px' }} icon={<Icon type={'terminal'} color={iconDisabledColor} />}>
            To test call tool (you will need Bearer token (Mergado) for the project to test): <span className={'clickable'} title={'copy to clipboard'} onClick={() => this.copyToClipboard(callCurlCommand(toolName, argumentsJson))}>
                <Icon type={'clipboard'} size={16} color={copiedText === callCurlCommand(toolName, argumentsJson) ? '#ccc' : Theme.blue} />
            </span>
            <pre style={{ whiteSpace: 'pre-wrap' }}>
                <code>{callCurlCommand(toolName, argumentsJson)}</code>
            </pre>
        </PopupHint>

        const renderResourceCurlCommandHelp = (resource: MCPResource) => <PopupHint style={{ position: 'absolute', top: '0px', right: '5px' }} icon={<Icon type={'terminal'} color={iconDisabledColor} />}>
            To test read resource (you will need Bearer token (Mergado) for the project to test): <span className={'clickable'} title={'copy to clipboard'} onClick={() => this.copyToClipboard(readResourceCurlCommand(resource))}>
                <Icon type={'clipboard'} size={16} color={copiedText === readResourceCurlCommand(resource) ? '#ccc' : Theme.blue} />
            </span>
            <pre style={{ whiteSpace: 'pre-wrap' }}>
                <code>{readResourceCurlCommand(resource)}</code>
            </pre>
        </PopupHint>

        return (
            <React.Fragment>
                <StyledSection
                    prefix={isLoadingTools ? <Spinner type='bubbles' /> : 'MCP Tools List'}
                    header={<Grid cols={'auto auto'}>
                        <GridCell title={'tools list response'} style={{ alignSelf: 'center' }}>
                            <Grid cols={'max-content 20px 20px'} gap={'6px'} style={{ '.muk-gridcell': { textAlign: 'center', alignSelf: 'center', cursor: 'pointer' } }}>
                                <Badge>{mcpRoot}</Badge>

                                <GridCell>
                                    <PopupHint icon={<Icon type={'terminal'} color={iconDisabledColor} />}>
                                        To test this code, you can use: <span className={'clickable'} title={'copy to clipboard'} onClick={() => this.copyToClipboard(toolsListCurlCommand)}>
                                            <Icon type={'clipboard'} size={16} color={copiedText === toolsListCurlCommand ? '#ccc' : Theme.blue} />
                                        </span>

                                        <pre style={{ whiteSpace: 'pre-wrap' }}>
                                            <code>{toolsListCurlCommand}</code>
                                        </pre>
                                    </PopupHint>
                                </GridCell>

                                <GridCell style={{ paddingTop: '10px' }}>
                                    <PopupHint icon={<svg viewBox="0 0 130 130" width="15px" height="15px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="Claude">
                                        <path d="M54.375 118.75L56.125 111L58.125 101L59.75 93L61.25 83.125L62.125 79.875L62 79.625L61.375 79.75L53.875 90L42.5 105.375L33.5 114.875L31.375 115.75L27.625 113.875L28 110.375L30.125 107.375L42.5 91.5L50 81.625L54.875 76L54.75 75.25H54.5L21.5 96.75L15.625 97.5L13 95.125L13.375 91.25L14.625 90L24.5 83.125L49.125 69.375L49.5 68.125L49.125 67.5H47.875L43.75 67.25L29.75 66.875L17.625 66.375L5.75 65.75L2.75 65.125L0 61.375L0.25 59.5L2.75 57.875L6.375 58.125L14.25 58.75L26.125 59.5L34.75 60L47.5 61.375H49.5L49.75 60.5L49.125 60L48.625 59.5L36.25 51.25L23 42.5L16 37.375L12.25 34.75L10.375 32.375L9.625 27.125L13 23.375L17.625 23.75L18.75 24L23.375 27.625L33.25 35.25L46.25 44.875L48.125 46.375L49 45.875V45.5L48.125 44.125L41.125 31.375L33.625 18.375L30.25 13L29.375 9.75C29.0417 8.625 28.875 7.375 28.875 6L32.75 0.750006L34.875 0L40.125 0.750006L42.25 2.625L45.5 10L50.625 21.625L58.75 37.375L61.125 42.125L62.375 46.375L62.875 47.75H63.75V47L64.375 38L65.625 27.125L66.875 13.125L67.25 9.125L69.25 4.375L73.125 1.87501L76.125 3.25L78.625 6.875L78.25 9.125L76.875 18.75L73.875 33.875L72 44.125H73.125L74.375 42.75L79.5 36L88.125 25.25L91.875 21L96.375 16.25L99.25 14H104.625L108.5 19.875L106.75 26L101.25 33L96.625 38.875L90 47.75L86 54.875L86.375 55.375H87.25L102.125 52.125L110.25 50.75L119.75 49.125L124.125 51.125L124.625 53.125L122.875 57.375L112.625 59.875L100.625 62.25L82.75 66.5L82.5 66.625L82.75 67L90.75 67.75L94.25 68H102.75L118.5 69.125L122.625 71.875L125 75.125L124.625 77.75L118.25 80.875L109.75 78.875L89.75 74.125L83 72.5H82V73L87.75 78.625L98.125 88L111.25 100.125L111.875 103.125L110.25 105.625L108.5 105.375L97 96.625L92.5 92.75L82.5 84.375H81.875V85.25L84.125 88.625L96.375 107L97 112.625L96.125 114.375L92.875 115.5L89.5 114.875L82.25 104.875L74.875 93.5L68.875 83.375L68.25 83.875L64.625 121.625L63 123.5L59.25 125L56.125 122.625L54.375 118.75Z" fill="#D97757">
                                        </path>
                                    </svg>
                                    }>
                                        How to add mcp to claude: <span className={'clickable'} title={'copy to clipboard'} onClick={() => this.copyToClipboard(addToClaudeCommand)}>
                                            <Icon type={'clipboard'} size={16} color={copiedText === addToClaudeCommand ? '#ccc' : Theme.blue} />
                                        </span>

                                        <pre style={{ whiteSpace: 'pre-wrap' }}>
                                            <code>{addToClaudeCommand}</code>
                                        </pre>

                                        To test connection:
                                        <pre style={{ whiteSpace: 'pre-wrap' }}>
                                            <code>claude mcp list</code>
                                        </pre>
                                    </PopupHint>
                                </GridCell>
                            </Grid>
                        </GridCell>
                        {toolsList &&
                            <JsonEditor
                                data={toolsList}
                                viewOnly={true}
                                collapse={true}
                                rootFontSize={10}
                                rootName=''
                            />
                        }
                    </Grid>
                    }>
                    <Grid cols={'900px 350px auto'} style={{ fontFamily: 'DejaVu Sans Mono' }} gap={'15px'}>
                        <GridCell>
                            {toolsList &&
                                <div className='muk-table-grid'>
                                    <Grid cols='minmax(200px, max-content) minmax(250px, auto) minmax(250px, auto)' className='muk-table'>
                                        <GridCell className='m-header-cell'>name</GridCell>
                                        <GridCell className='m-header-cell'>description</GridCell>
                                        <GridCell className='m-header-cell'>arguments</GridCell>
                                        <React.Fragment>
                                            {toolsList?.result?.tools?.map(tool => <TRow key={`${tool.name}_${request?.name}_${JSON.stringify(args)}`} className={`clickable ${tool.name === request?.name ? 'active' : ''}`}>
                                                <GridCell className='muk-cell row-even clickable' align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden', position: 'relative' }} onClick={() => this.setRequest(tool)}>
                                                    {tool.name}
                                                    {renderCurlCommandHelp(tool.name, JSON.stringify(args))}
                                                </GridCell>
                                                <GridCell align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setRequest(tool)}>{tool.description}</GridCell>
                                                <GridCell align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setRequest(tool)}>
                                                    {Object.keys(tool.inputSchema.properties).length > 0 ?
                                                        <JsonEditor
                                                            data={tool.inputSchema.properties}
                                                            rootName=''
                                                            rootFontSize={10}
                                                            showCollectionCount='when-closed'
                                                            viewOnly={true}
                                                            collapse={1}
                                                            indent={6}
                                                        /> :
                                                        <Badge secondary={true}>no params</Badge>
                                                    }
                                                </GridCell>
                                            </TRow>)
                                            }
                                        </React.Fragment>
                                    </Grid>
                                </div>
                            }
                        </GridCell>
                        <GridCell>
                            <h3 style={{ textAlign: 'center' }}>input</h3>

                            {request &&
                                <React.Fragment>
                                    <div style={{ marginBottom: '25px' }}>tool name: <Badge>{request?.name}</Badge></div>

                                    <JsonEditor
                                        data={args}
                                        setData={(value) => this.setState({ args: value })}
                                        rootName=''
                                        rootFontSize={10}
                                        showCollectionCount='when-closed'
                                        enableClipboard={false}
                                        onEditEvent={(e) => {
                                            this.setState({ isEditing: !!e })
                                        }}
                                    />

                                    {!isEditing &&
                                        <div style={{ marginTop: '25px', textAlign: 'right' }}>
                                            <Button type='void' onClick={() => this.sendRequest(request)}>send</Button>
                                        </div>
                                    }
                                </React.Fragment>
                            }
                        </GridCell>
                        <GridCell>
                            <h3 style={{ textAlign: 'center' }}>output</h3>

                            {statusCode &&
                                <div style={{ marginBottom: '25px', position: 'relative' }}>
                                    status code: {statusCode}
                                    {output?.result?.isError &&
                                        <span style={{
                                            position: 'absolute',
                                            top: '0px',
                                            right: '10px',
                                        }}>
                                            <Icon type={'exclamation-triangle-radix'} color={Theme.orange} text={'ERROR'} />
                                        </span>
                                    }
                                </div>
                            }

                            {output && request &&
                                <JsonEditor
                                    data={output}
                                    viewOnly={true}
                                    collapse={false}
                                    enableClipboard={true}
                                    rootName=''
                                    rootFontSize={10}
                                    stringTruncate={4096}
                                />
                            }
                        </GridCell>
                    </Grid>
                </StyledSection>


                <StyledSection
                    prefix={isLoadingResources ? <Spinner type='bubbles' /> : 'MCP Resources List'}
                    header={<Grid cols={'auto auto'}>
                        <GridCell title={'resources list response'} style={{ alignSelf: 'center' }}></GridCell>
                    </Grid>}
                    suffix={resourcesList &&
                        <JsonEditor
                            data={resourcesList}
                            viewOnly={true}
                            collapse={true}
                            rootFontSize={10}
                            rootName=''
                        />
                    }>
                    <Grid cols={'auto 150px 150px'} style={{ fontFamily: 'DejaVu Sans Mono' }} gap={'15px'}>
                        <GridCell>
                            {resourcesList &&
                                <div className='muk-table-grid'>
                                    <Grid cols='repeat(6, auto)' className='muk-table'>
                                        <GridCell className='m-header-cell'>uri</GridCell>
                                        <GridCell className='m-header-cell'>name</GridCell>
                                        <GridCell className='m-header-cell'>title</GridCell>
                                        <GridCell className='m-header-cell'>description</GridCell>
                                        <GridCell className='m-header-cell'>mimeType</GridCell>
                                        <GridCell className='m-header-cell'>details</GridCell>
                                        {resourcesList?.result?.resources?.map(r => <TRow key={`${r.uri}_${resource?.uri}`} className={`clickable ${r.uri === resource?.uri ? 'active' : ''}`}>
                                            <GridCell className='muk-cell row-even clickable' align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden', position: 'relative' }} onClick={() => this.setResource(r)}>
                                                {r.uri}
                                                {renderResourceCurlCommandHelp(r)}
                                            </GridCell>
                                            <GridCell align='left' style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setResource(r)}>{r.name}</GridCell>
                                            <GridCell align='left' style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setResource(r)}>{r.title}</GridCell>
                                            <GridCell align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setResource(r)}>{r.description}</GridCell>
                                            <GridCell align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setResource(r)}>{r.mimeType}</GridCell>
                                            <GridCell align="left" style={{ whiteSpace: 'pre-wrap', fontSize: 'small', overflow: 'hidden' }} onClick={() => this.setResource(r)}>
                                                <JsonEditor
                                                    data={r}
                                                    rootName=''
                                                    rootFontSize={10}
                                                    showCollectionCount='when-closed'
                                                    viewOnly={true}
                                                    collapse={1}
                                                    indent={6}
                                                />
                                            </GridCell>
                                        </TRow>)
                                        }
                                    </Grid>
                                </div>
                            }
                        </GridCell>
                        <GridCell>
                            <h3 style={{ textAlign: 'center' }}>input</h3>
                            {resource?.uri &&
                                <div>
                                    {resource.uri}

                                    <div style={{ marginBottom: '25px' }}>resource name: <Badge>{resource.name}</Badge></div>

                                    {args &&
                                        <JsonEditor
                                            data={args}
                                            setData={(value) => this.setState({ args: value })}
                                            rootName=''
                                            rootFontSize={10}
                                            showCollectionCount='when-closed'
                                            enableClipboard={false}
                                            onEditEvent={(e) => {
                                                this.setState({ isEditing: !!e })
                                            }}
                                        />
                                    }

                                    {!isEditing &&
                                        <div style={{ marginTop: '25px', textAlign: 'right' }}>
                                            <Button type='void' onClick={() => this.readResource()}>send</Button>
                                        </div>
                                    }
                                </div>
                            }
                        </GridCell>
                        <GridCell>
                            <h3 style={{ textAlign: 'center' }}>output</h3>

                            {statusCode && <div style={{ marginBottom: '25px', position: 'relative' }}>
                                status code: {statusCode}
                                {output?.result?.isError &&
                                    <span style={{
                                        position: 'absolute',
                                        top: '0px',
                                        right: '10px',
                                    }}>
                                        <Icon type={'exclamation-triangle-radix'} color={Theme.orange} text={'ERROR'} />
                                    </span>
                                }
                            </div>}

                            {output && resource &&
                                <JsonEditor
                                    data={output}
                                    viewOnly={true}
                                    collapse={false}
                                    enableClipboard={true}
                                    rootName=''
                                    rootFontSize={10}
                                    stringTruncate={4096}
                                />
                            }
                        </GridCell>
                    </Grid>
                </StyledSection>
            </React.Fragment>
        )
    }
}

const StyledSection = css(Section)({
    ' .clickable': {
        color: Theme.steelblue,
        '&:hover': {
            background: '#eeeeee',
        }
    },
    ' .active': {
        background: `${Theme.blue} !important`,
        color: `#ffffff !important`,
    },
    ' h3': {
        marginBottom: '25px',
    },
    ' .jer-editor-container': {
        fontSize: 'small',
    },
    ' .m-header-cell': {
        borderRadius: '10px 10px 0px 0px',
        borderWidth: '1px 1px 0px',
        borderStyle: 'solid solid none',
        borderColor: 'rgb(204, 204, 204)',
        background: '#eeeeee',
        padding: '5px 10px',
        textAlign: 'center',
        fontSize: 'small',
    },
    ' .muk-cell': {
        padding: '5px 10px',
        border: '1px solid rgb(204, 204, 204)',
    }
})


export default MCP